import React, {type ReactNode, Component} from "react"
import {
    BufferGeometry,
    type Camera,
    Line,
    LineBasicMaterial,
    Object3D,
    Vector3,
    Vector2,
} from "three"
import {CSS2DObject} from "three/examples/jsm/renderers/CSS2DRenderer.js"
import * as ReactDOM from "react-dom/client"
import {renderToStaticMarkup} from "react-dom/server"
import {RAD2DEG} from "three/src/math/MathUtils"

export enum TapHelperLabelKind {
    Vertical,
    Left,
    Right,
}

export class TwoPointPin extends Object3D {
    label: CSS2DObject
    line: CSS2DObject
    // labelRoot: ReactDOM.Root
    // lineRoot: ReactDOM.Root
    labelContainer: HTMLElement
    lineContainer: HTMLElement
    #batonRootElement: HTMLDivElement
    #batonLineElement: HTMLDivElement
    #labelRadius: number
    #tmp1Vector3: Vector3
    #tmp2Vector3: Vector3
    #tmp1Vector2: Vector2
    #tmp2Vector2: Vector2

    constructor(
        point1: Vector3,
        point2: Vector3,
        camera: Camera,
        label: string,
        labelContainer: HTMLElement,
        lineContainer: HTMLElement,
        onClick: () => void
    ) {
        super()
        this.#labelRadius = 25

        // Create Bubble CSS2DObject
        this.label = new CSS2DObject(labelContainer)
        this.label.position.copy(point1)
        this.add(this.label)

        // Create line CSS2DObject
        this.line = new CSS2DObject(lineContainer)
        this.line.position.copy(point2)
        this.add(this.line)

        // Create roots
        this.labelContainer = labelContainer
        this.lineContainer = lineContainer
        this.renderHTML(label, this.#labelRadius, onClick, 100, 3, 0)

        this.updateLine(point1, point2, camera)
    }

    /**
     * Update the angle and length of the line to connect point1 to point2 in screen space
     * @param point1
     * @param point2
     * @param camera
     */
    updateLine(point1: Vector3, point2: Vector3, camera: Camera): void {
        // Calculate angle and length required to connect point1 and point2 in screen space
        this.#tmp1Vector3 = new Vector3().copy(point1)
        this.#tmp2Vector3 = new Vector3().copy(point2)
        // Convert to NDC
        this.#tmp1Vector3.project(camera)
        this.#tmp2Vector3.project(camera)
        // Convert to screen space
        this.#tmp1Vector2 = new Vector2(
            (this.#tmp1Vector3.x / 2 + 0.5) * window.innerWidth,
            (this.#tmp1Vector3.y / 2 + 0.5) * window.innerHeight
        )
        this.#tmp2Vector2 = new Vector2(
            (this.#tmp2Vector3.x / 2 + 0.5) * window.innerWidth,
            (this.#tmp2Vector3.y / 2 + 0.5) * window.innerHeight
        )
        // console.log(this.#tmp2Vector2)
        this.#tmp1Vector2.sub(this.#tmp2Vector2)
        const angle = 180 - this.#tmp1Vector2.angle() * RAD2DEG
        const length = this.#tmp1Vector2.length() - this.#labelRadius
        this.#batonRootElement.style.transform = `translateX(-50%) rotate(${angle}deg)`
        this.#batonLineElement.style.transform = `scaleX(${length}%)`
    }

    renderHTML(
        label: string,
        radius: number,
        onClick: () => void,
        width: number,
        height: number,
        angle: number
    ): void {
        this.lineContainer.innerHTML = renderToStaticMarkup(
            <CSSBaton
                width={width}
                height={height}
                startColor="#2E3541"
                endColor="#2E354140"
                angle={angle}
            />
        )
        this.labelContainer.innerHTML = renderToStaticMarkup(
            <LabelBubble
                label={label}
                radius={this.#labelRadius}
                onClick={onClick}
            />
        )
        // Extract HTML elements from rendered HTML
        {
            const tmp =
                this.line.element.querySelector<HTMLDivElement>(
                    ".pin-baton-root"
                )
            if (tmp === null) throw Error("could not find pin baton root")
            this.#batonRootElement = tmp
        }
        {
            const tmp =
                this.line.element.querySelector<HTMLDivElement>(
                    ".pin-baton-line"
                )
            if (tmp === null) throw Error("could not find pin baton line")
            this.#batonLineElement = tmp
        }
        // renderToStaticMarkup doesn't attach event listers, so we have to do it manually
        {
            const tmp =
                this.label.element.querySelector<HTMLDivElement>(".label-link")
            if (tmp === null) throw Error("could not find label link")
            tmp.addEventListener("click", onClick)
        }
    }
}

export interface TapHelperProps {
    label: string
    onClick: () => void
    kind: TapHelperLabelKind
    size: number
}

interface State {
}

class TapHelperLabel extends Component<TapHelperProps, State> {
    render(): JSX.Element {
        if (this.props.kind === TapHelperLabelKind.Vertical) {
            return (
                <ColumnDiv>
                    <LabelBubble
                        label={this.props.label}
                        onClick={this.props.onClick}
                        radius={25}
                    />
                    <img src="assets/baton.svg" alt="baton-svg"></img>
                </ColumnDiv>
            )
        } else if (this.props.kind === TapHelperLabelKind.Left) {
            return (
                <RTLRowDiv>
                    <LabelBubble
                        label={this.props.label}
                        onClick={this.props.onClick}
                    />
                    <CSSBaton
                        width={this.props.size}
                        height={3}
                        startColor="#2E3541"
                        endColor="#2E354140"
                        angle={0}
                    />
                </RTLRowDiv>
            )
        } else {
            return (
                <ColumnDiv>
                    <LabelBubble
                        label={this.props.label}
                        onClick={this.props.onClick}
                    />
                    <img src="assets/baton.svg" alt="baton-svg"></img>
                </ColumnDiv>
            )
        }
    }
}

// class StretchyBaton extends Component<{
//     height: number
//     width: number
//     startColor: string
//     endColor: string
//     angleDeg: AngleDegrees
// }> {}

class CSSBaton extends Component<{
    height: number
    width: number
    startColor: string
    endColor: string
    angle: AngleDegrees
}> {
    render(): JSX.Element {
        return (
            <div
                className="pin-baton-root"
                style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                    transformOrigin: "right",
                    transform: `translateX(-50%) rotate(${this.props.angle}deg)`,
                }}>
                {/* Baton line */}
                <div
                    className="pin-baton-line"
                    style={{
                        height: this.props.height,
                        width: "100px",
                        transformOrigin: "right",
                        transform: `scaleX(${this.props.width}%)`,
                        backgroundImage: `linear-gradient(45deg, ${this.props.startColor}, ${this.props.startColor}, ${this.props.endColor})`,
                    }}></div>
                {/* Baton circle */}
                <div
                    style={{
                        height: this.props.height * 3,
                        width: this.props.height * 3,
                        backgroundColor: this.props.endColor,
                        borderRadius: this.props.height * 1.5,
                    }}></div>
            </div>
        )
    }
}

class ColumnDiv extends Component<{ children: ReactNode }> {
    render(): JSX.Element {
        return (
            <div
                className="label globe-pin"
                style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    transform: "translateY(-50%)",
                }}>
                {this.props.children}
            </div>
        )
    }
}

class RTLRowDiv extends Component<{ children: ReactNode }> {
    render(): JSX.Element {
        return (
            <div
                className="label globe-pin"
                style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                    transform: "translateX(-50%)",
                }}>
                {this.props.children}
            </div>
        )
    }
}

class LabelBubble extends Component<{
    label: string
    radius: number
    onClick: () => void
}> {
    render(): JSX.Element {
        return (
            <span
                className="label-link"
                style={{pointerEvents: "all"}}
                onClick={this.props.onClick}>
                <div
                    style={{
                        userSelect: "none",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center",
                        color: "white",
                        backgroundColor: "#2E3541",
                        width: `${this.props.radius * 2}px`,
                        height: `${this.props.radius * 2}px`,
                        borderRadius: `${this.props.radius}px`,
                        fontFamily: "sans-serif",
                        fontWeight: 500,
                        fontSize: 22,
                    }}>
                    {this.props.label}
                </div>
            </span>
        )
    }
}

export default TapHelperLabel
